import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

import styles from './TableTabs.module.css';

export type MoveAnchor = { after_id: string } | { before_id: string };

const tabData = (databaseId: string, tableId: string) => ({
  type: 'database-table',
  databaseId,
  tableId,
});
const isTab = (data: Record<string | symbol, unknown>): data is ReturnType<typeof tabData> =>
  data.type === 'database-table' &&
  typeof data.databaseId === 'string' &&
  typeof data.tableId === 'string';

/**
 * One table tab. For those who manage the structure it is draggable: a bar shows where it would
 * land (left / right of the tab it is over) and ONLY the completed drop is sent, as a move next to
 * a neighbor by id (never an index). A tab of another database is not a valid source, a drop where
 * the tab already is saves nothing, and the click that ends a drag never switches the table.
 */
export function TabSlot({
  databaseId,
  tableId,
  previousId,
  nextId,
  enabled,
  onDropBeside,
  children,
  ...rest
}: {
  databaseId: string;
  tableId: string;
  previousId: string | undefined;
  nextId: string | undefined;
  enabled: boolean;
  onDropBeside: (sourceId: string, anchor: MoveAnchor) => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  'data-active'?: boolean | undefined;
  'data-color'?: string | undefined;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;
    return combine(
      draggable({
        element,
        getInitialData: () => tabData(databaseId, tableId),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          isTab(source.data) &&
          source.data.databaseId === databaseId &&
          source.data.tableId !== tableId,
        getData: ({ input }) =>
          attachClosestEdge(tabData(databaseId, tableId), {
            element,
            input,
            allowedEdges: ['left', 'right'],
          }),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: ({ self, source }) => {
          setEdge(null);
          if (!isTab(source.data)) return;
          const side = extractClosestEdge(self.data);
          const sourceId = source.data.tableId;
          if (side === 'left' && previousId !== sourceId)
            onDropBeside(sourceId, { before_id: tableId });
          if (side === 'right' && nextId !== sourceId)
            onDropBeside(sourceId, { after_id: tableId });
        },
      }),
    );
  }, [databaseId, tableId, previousId, nextId, enabled, onDropBeside]);

  const { className, ...more } = rest;
  return (
    <div
      ref={ref}
      {...more}
      className={[
        className,
        enabled && styles.draggable,
        dragging && styles.dragging,
        edge && styles[`drop-${edge}`],
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}
